import React, { useEffect, useRef, useState, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import {
  Cloud, Check, AlertTriangle, RefreshCw, X, ExternalLink,
  Images, User, LayoutGrid, ChevronRight, StopCircle,
} from 'lucide-react';
import toast from 'react-hot-toast';
import { Modal } from '../UI/Modal';
import { googlePhotosApi, SyncJob, SyncScope } from '../../api/googlePhotos';
import styles from './GooglePhotosSyncButton.module.css';

// ─── Google Photos logo from CDN ──────────────────────────────────────────────
const GP_LOGO = 'https://d3ic3ju44zc444.cloudfront.net/assets/LogoImages/google-photos-6.svg';

function GooglePhotosLogo({ size = 18 }: { size?: number }) {
  return (
    <img
      src={GP_LOGO}
      width={size}
      height={size}
      alt="Google Photos"
      draggable={false}
      style={{ display: 'block', flexShrink: 0 }}
    />
  );
}

// Spinning icon via framer-motion (avoids Tailwind animate-spin dependency)
function SpinIcon({ size = 12 }: { size?: number }) {
  return (
    <motion.span
      style={{ display: 'inline-flex' }}
      animate={{ rotate: 360 }}
      transition={{ duration: 0.9, repeat: Infinity, ease: 'linear' }}
    >
      <RefreshCw size={size} />
    </motion.span>
  );
}

// ─── Types ────────────────────────────────────────────────────────────────────
type ModalView = 'closed' | 'pre-connect' | 'options' | 'progress';

export interface GooglePhotosSyncButtonProps {
  albumId: string;
  totalAlbumPhotos: number;
  faceEnabled: boolean;
  hasFacePhoto: boolean;
  myPhotoIds: Set<string>;
  /** True when this viewer is restricted to highlights/pins + their own face matches
   *  (guestVisibility === 'matched_only' and they're not the creator/in creator mode). */
  restrictedView?: boolean;
  /** The exact photo IDs a restricted viewer sees in the All Photos tab — used as the
   *  "everything" set instead of the full album when restrictedView is true. */
  restrictedAllPhotoIds?: Set<string>;
  /** Called when user chooses a "pick photos" option — parent enters select mode */
  onEnterSelectMode: (filter: 'all' | 'my_photos') => void;
  /** Incremented by parent when a sync is triggered externally (e.g. from batch bar) */
  externalJobTrigger?: number;
  /** Incremented by parent to auto-open the options modal (e.g. after OAuth callback) */
  autoOpenOptions?: number;
  /** Called whenever the internal job state changes — lets parent show a status line */
  onJobChange?: (job: SyncJob | null) => void;
  /** Photo IDs already synced to Google Photos — used to warn on re-sync */
  syncedPhotoIds?: Set<string>;
  /** Called immediately after a sync job is successfully started — for optimistic UI updates */
  onSyncStarted?: (scope: SyncScope, photoIds?: string[]) => void;
}

const POLL_MS = 2500;

// ─── Main component ───────────────────────────────────────────────────────────
export function GooglePhotosSyncButton({
  albumId,
  totalAlbumPhotos,
  faceEnabled,
  hasFacePhoto,
  myPhotoIds,
  restrictedView = false,
  restrictedAllPhotoIds,
  onEnterSelectMode,
  externalJobTrigger = 0,
  autoOpenOptions = 0,
  onJobChange,
  syncedPhotoIds,
  onSyncStarted,
}: GooglePhotosSyncButtonProps) {
  const [connected, setConnected] = useState<boolean | null>(null);
  const [job, setJob] = useState<SyncJob | null>(null);
  const [modalView, setModalView] = useState<ModalView>('closed');
  const [cancelling, setCancelling] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const pollRef = useRef<ReturnType<typeof setInterval> | null>(null);
  const tooltipTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const isActive = job?.status === 'pending' || job?.status === 'running';

  // ─── Polling ─────────────────────────────────────────────────────────────
  const stopPolling = useCallback(() => {
    if (pollRef.current) { clearInterval(pollRef.current); pollRef.current = null; }
  }, []);

  const fetchStatus = useCallback(async () => {
    try {
      const [statusRes, jobRes] = await Promise.all([
        googlePhotosApi.getStatus(),
        googlePhotosApi.getSyncStatus(albumId),
      ]);
      setConnected(statusRes.connected);
      setJob(jobRes.job);
      return jobRes.job;
    } catch {
      return null;
    }
  }, [albumId]);

  useEffect(() => { fetchStatus(); }, [fetchStatus]);

  useEffect(() => {
    if (externalJobTrigger > 0) fetchStatus();
  }, [externalJobTrigger, fetchStatus]);

  // Auto-open options modal after OAuth callback
  useEffect(() => {
    if (autoOpenOptions > 0) setModalView('options');
  }, [autoOpenOptions]);

  // Notify parent whenever job state changes (for status line in header)
  useEffect(() => {
    onJobChange?.(job);
  }, [job, onJobChange]);

  useEffect(() => {
    if (!isActive) { stopPolling(); return; }

    pollRef.current = setInterval(async () => {
      const latest = await fetchStatus();
      if (
        !latest ||
        latest.status === 'completed' ||
        latest.status === 'failed' ||
        latest.status === 'cancelled'
      ) {
        stopPolling();
      }
    }, POLL_MS);

    return stopPolling;
  }, [isActive, fetchStatus, stopPolling]);

  // ─── Handlers ────────────────────────────────────────────────────────────
  const handleButtonClick = () => {
    if (isActive) {
      setModalView('progress');
    } else if (!connected) {
      setModalView('pre-connect');
    } else {
      setModalView('options');
    }
  };

  const handleStartSync = async (scope: SyncScope, photoIds?: string[]) => {
    // Warn if any of the photos being synced have been synced before
    if (syncedPhotoIds && syncedPhotoIds.size > 0) {
      const idsToCheck = photoIds ?? [];
      const alreadySynced = scope === 'all'
        ? syncedPhotoIds.size
        : idsToCheck.filter((id) => syncedPhotoIds.has(id)).length;
      if (alreadySynced > 0) {
        toast(`${alreadySynced} photo${alreadySynced !== 1 ? 's' : ''} already synced — uploading again will create duplicates in Google Photos`, {
          duration: 4000,
        });
      }
    }
    setModalView('progress');
    try {
      await googlePhotosApi.startSync(albumId, { scope, photoIds });
      onSyncStarted?.(scope, photoIds);
      await fetchStatus();
    } catch {
      toast.error('Failed to start sync. Please try again.');
      setModalView('options');
    }
  };

  const handleCancel = async () => {
    setCancelling(true);
    try {
      await googlePhotosApi.cancelSync(albumId);
      await fetchStatus();
      setModalView('closed');
      toast('Sync cancelled');
    } catch {
      toast.error('Could not cancel sync');
    } finally {
      setCancelling(false);
    }
  };

  const handlePickPhotos = (filter: 'all' | 'my_photos') => {
    setModalView('closed');
    onEnterSelectMode(filter);
  };

  const handleMouseEnter = () => {
    tooltipTimerRef.current = setTimeout(() => setShowTooltip(true), 400);
  };
  const handleMouseLeave = () => {
    if (tooltipTimerRef.current) clearTimeout(tooltipTimerRef.current);
    setShowTooltip(false);
  };

  // ─── Derived display ──────────────────────────────────────────────────────
  const progress = job && job.totalPhotos > 0
    ? Math.round((job.syncedPhotos / job.totalPhotos) * 100)
    : 0;

  const buttonIcon = (() => {
    if (job?.status === 'running' || job?.status === 'pending') return <SpinIcon size={16} />;
    if (job?.status === 'completed') return <GooglePhotosLogo size={16} />;
    if (job?.status === 'failed') return <AlertTriangle size={16} />;
    return <GooglePhotosLogo size={16} />;
  })();

  const buttonText = (() => {
    if (job?.status === 'running' || job?.status === 'pending') return 'Syncing to Google Photos...';
    if (job?.status === 'completed') return 'Sync more to Google Photos';
    if (job?.status === 'failed') return 'Sync failed';
    return 'Sync to Google Photos';
  })();

  const tooltipText = (() => {
    if (!job || job.status === 'cancelled') return 'Sync to Google Photos';
    if (job.status === 'running') return `Syncing — ${job.syncedPhotos} of ${job.totalPhotos} photos uploaded`;
    if (job.status === 'pending') return 'Sync is starting…';
    if (job.status === 'completed') {
      return job.syncedPhotos > 0
        ? `${job.syncedPhotos} photos synced — click for details`
        : 'Sync complete — click for details';
    }
    if (job.status === 'failed') return `Sync failed: ${job.error ?? 'unknown error'}`;
    return 'Sync to Google Photos';
  })();

  const iconBtnStateClass = (() => {
    if (job?.status === 'running' || job?.status === 'pending') return styles['iconBtnSyncing'];
    if (job?.status === 'completed') return styles['iconBtnDone'];
    if (job?.status === 'failed') return styles['iconBtnFailed'];
    return styles['iconBtnIdle'];
  })();

  const statusDotClass = (() => {
    if (job?.status === 'running' || job?.status === 'pending') return styles['statusDotSyncing'];
    if (job?.status === 'completed') return styles['statusDotDone'];
    if (job?.status === 'failed') return styles['statusDotFailed'];
    return null;
  })();

  return (
    <>
      {/* ── Trigger button (in header row) ────────────── */}
      <div className={styles['triggerWrap']}>
        <motion.button
          type="button"
          onClick={handleButtonClick}
          className={[styles['iconBtn'], iconBtnStateClass].join(' ')}
          whileTap={{ scale: 0.95 }}
          transition={{ type: 'spring', stiffness: 500, damping: 28 }}
        >
          {buttonIcon}
          <span>{buttonText}</span>
          {/* Status dot — hidden when idle */}
          {statusDotClass && (
            <span className={[styles['statusDot'], statusDotClass].join(' ')} />
          )}
        </motion.button>
      </div>

      {/* ── Pre-connect modal ───────────────────────────────────────────── */}
      <Modal
        isOpen={modalView === 'pre-connect'}
        onClose={() => setModalView('closed')}
        title="Connect Google Photos"
        size="sm"
      >
        <div className={styles['connectBody']}>
          {/* Hero: clay icon orb + permission badge */}
          <div className={styles['connectHero']}>
            <div className={styles['connectIconOrb']}>
              <GooglePhotosLogo size={32} />
            </div>
            <div className={styles['connectBadge']}>
              One-time permission
            </div>
          </div>

          <p className={styles['connectDesc']}>
            We'll ask Google to let us <strong>add photos</strong> to your library.
            We can only upload — we can never view, edit, or delete your existing photos.
          </p>

          {/* Trust checklist card */}
          <div className={styles['trustCard']}>
            <div className={styles['trustRow']}>
              <div className={styles['trustCheck']}><Check size={11} /></div>
              <span className={styles['trustText']}>Only adds new photos — never modifies existing ones</span>
            </div>
            <div className={styles['trustRow']}>
              <div className={styles['trustCheck']}><Check size={11} /></div>
              <span className={styles['trustText']}>Permission can be revoked anytime in Google settings</span>
            </div>
            <div className={styles['trustRow']}>
              <div className={styles['trustCheck']}><Check size={11} /></div>
              <span className={styles['trustText']}>Works with your existing Google account — no new sign-up</span>
            </div>
          </div>

          {/* 3D clay CTA */}
          <a
            href={googlePhotosApi.connectUrl(albumId)}
            className={styles['connectCta']}
          >
            <GooglePhotosLogo size={18} />
            Continue to Google
            <span className={styles['connectCtaArrow']}><ChevronRight size={16} /></span>
          </a>
        </div>
      </Modal>

      {/* ── Sync options modal ──────────────────────────────────────────── */}
      <Modal
        isOpen={modalView === 'options'}
        onClose={() => setModalView('closed')}
        title="Sync to Google Photos"
        size="sm"
      >
        <div className={styles['optionsBody']}>
          <p className={styles['optionsSubtitle']}>
            A new album will be created in your Google Photos.
          </p>

          {/* ── Your photos section ─────────────────────────────────────── */}
          {faceEnabled && hasFacePhoto && (
            <>
              {/* Pick from my photos — primary recommended action */}
              <OptionCard
                icon={<User size={18} />}
                title="Pick from my photos"
                description="Hand-select exactly which of your photos to sync"
                badge="recommended"
                onClick={() => handlePickPhotos('my_photos')}
                featured
              />

              {/* Sync all my photos — automatic */}
              <OptionCard
                icon={<User size={18} />}
                title="Sync my photos"
                description={
                  myPhotoIds.size > 0
                    ? `${myPhotoIds.size} photos where you appear — uploaded instantly`
                    : 'No face-matched photos found yet'
                }
                onClick={() => handleStartSync('my_photos', Array.from(myPhotoIds))}
                disabled={myPhotoIds.size === 0}
                disabledHint="No face-matched photos found in this album yet"
              />
            </>
          )}

          {/* ── Divider ─────────────────────────────────────────────────── */}
          <div className={styles['sectionDivider']}>
            <div className={styles['sectionDividerLine']} />
            <span className={styles['sectionDividerText']}>
              {restrictedView ? 'or from all photos you can see' : 'or from the full album'}
            </span>
            <div className={styles['sectionDividerLine']} />
          </div>

          {/* Pick from all photos */}
          <OptionCard
            icon={<LayoutGrid size={18} />}
            title="Pick from all photos"
            description={
              restrictedView
                ? 'Browse the photos visible to you and choose what to sync'
                : 'Browse every photo in this album and choose what to sync'
            }
            badge="select"
            onClick={() => handlePickPhotos('all')}
            featured={!(faceEnabled && hasFacePhoto)}
          />

          {/* Sync everything — bulk fallback. For a restricted viewer this must only
              cover what they can actually see (highlights/pins + own matches), never
              the whole album. */}
          <OptionCard
            icon={<Images size={18} />}
            title="Sync everything"
            description={
              restrictedView
                ? `Upload all ${restrictedAllPhotoIds?.size ?? 0} photos you can see to Google Photos`
                : `Upload all ${totalAlbumPhotos} photos to Google Photos`
            }
            onClick={() =>
              restrictedView
                ? handleStartSync('selected', Array.from(restrictedAllPhotoIds ?? []))
                : handleStartSync('all')
            }
            disabled={restrictedView && (restrictedAllPhotoIds?.size ?? 0) === 0}
            disabledHint="No photos available to sync yet"
          />

          <button
            type="button"
            className={styles['disconnectBtn']}
            onClick={async () => {
              await googlePhotosApi.disconnect();
              setConnected(false);
              setModalView('closed');
              toast.success('Google Photos disconnected');
            }}
          >
            Disconnect Google Photos
          </button>
        </div>
      </Modal>

      {/* ── Progress / result modal ─────────────────────────────────────── */}
      <Modal
        isOpen={modalView === 'progress'}
        onClose={() => setModalView('closed')}
        title="Sync to Google Photos"
        size="sm"
      >
        <ProgressView
          job={job}
          cancelling={cancelling}
          onCancel={handleCancel}
          onRetry={() => setModalView('options')}
          onSyncMore={() => setModalView('options')}
          onClose={() => setModalView('closed')}
        />
      </Modal>
    </>
  );
}

// ─── Option card ──────────────────────────────────────────────────────────────
function OptionCard({
  icon, title, description, badge, onClick, disabled, disabledHint, featured,
}: {
  icon: React.ReactNode;
  title: string;
  description: string;
  /** 'recommended' renders a red pill, 'select' renders a neutral pill */
  badge?: 'recommended' | 'select';
  onClick: () => void;
  disabled?: boolean;
  disabledHint?: string;
  /** Stronger red tint — used for the primary CTA card */
  featured?: boolean;
}) {
  return (
    <motion.button
      type="button"
      onClick={disabled ? undefined : onClick}
      className={[
        styles['optionCard'],
        featured ? styles['optionCardFeatured'] : '',
        disabled ? styles['optionCardDisabled'] : '',
      ].filter(Boolean).join(' ')}
      whileTap={disabled ? {} : { scale: 0.98 }}
      transition={{ type: 'spring', stiffness: 500, damping: 28 }}
      title={disabled && disabledHint ? disabledHint : undefined}
    >
      <span className={styles['optionIconWrap']}>{icon}</span>
      <span className={styles['optionBody']}>
        <span className={styles['optionTitle']}>{title}</span>
        <span className={styles['optionDesc']}>{description}</span>
      </span>
      {badge === 'recommended' && (
        <span className={styles['recommendedBadge']}>Recommended</span>
      )}
      {badge === 'select' && (
        <span className={styles['optionBadge']}>Select</span>
      )}
      {!badge && !disabled && (
        <ChevronRight size={14} className={styles['optionArrow']} />
      )}
    </motion.button>
  );
}

// ─── Progress / result view ───────────────────────────────────────────────────
function ProgressView({
  job, cancelling, onCancel, onRetry, onSyncMore, onClose,
}: {
  job: SyncJob | null;
  cancelling: boolean;
  onCancel: () => void;
  onRetry: () => void;
  onSyncMore: () => void;
  onClose: () => void;
}) {
  const progress = job && job.totalPhotos > 0
    ? Math.round((job.syncedPhotos / job.totalPhotos) * 100)
    : 0;

  const isActive = job?.status === 'pending' || job?.status === 'running';
  const isDone = job?.status === 'completed';
  const isFailed = job?.status === 'failed';
  const isCancelled = job?.status === 'cancelled';

  const scopeLabel = {
    all: 'all album photos',
    my_photos: 'your face-matched photos',
    selected: 'your selected photos',
  }[job?.scope ?? 'all'];

  const statusIconClass = [
    styles['statusIcon'],
    isActive ? styles['statusIconRunning'] : '',
    isDone ? styles['statusIconDone'] : '',
    isFailed ? styles['statusIconFailed'] : '',
    isCancelled ? styles['statusIconCancelled'] : '',
  ].filter(Boolean).join(' ');

  return (
    <div className={styles['progressBody']}>
      {/* Status row */}
      <div className={styles['statusRow']}>
        <div className={statusIconClass}>
          {isActive && <SpinIcon size={16} />}
          {isDone && <Check size={16} />}
          {isFailed && <AlertTriangle size={16} />}
          {isCancelled && <X size={16} />}
        </div>
        <div>
          <p className={styles['statusTitle']}>
            {isActive && 'Syncing…'}
            {isDone && 'Sync complete'}
            {isFailed && 'Sync failed'}
            {isCancelled && 'Sync cancelled'}
            {!job && 'Starting sync…'}
          </p>
          {job && (
            <p className={styles['statusSub']}>
              {isActive ? `Uploading ${scopeLabel}` : scopeLabel}
            </p>
          )}
        </div>
      </div>

      {/* Progress bar */}
      {job && job.totalPhotos > 0 && (
        <div className={styles['barWrap']}>
          <div className={styles['barMeta']}>
            <span>{job.syncedPhotos} of {job.totalPhotos} photos</span>
            <span>{progress}%</span>
          </div>
          <div className={styles['barTrack']}>
            <motion.div
              className={[
                styles['barFill'],
                isDone ? styles['barFillDone'] : '',
              ].filter(Boolean).join(' ')}
              animate={{ width: `${isDone ? 100 : progress}%` }}
              transition={{ type: 'spring', stiffness: 80, damping: 18 }}
            />
          </div>
          {job.failedPhotos > 0 && (
            <p className={styles['barNote']}>
              {job.failedPhotos} photo{job.failedPhotos !== 1 ? 's' : ''} couldn't be uploaded
            </p>
          )}
        </div>
      )}

      {/* Error message */}
      {isFailed && job?.error && (
        <p className={styles['errorBox']}>{job.error}</p>
      )}

      {/* Open in Google Photos */}
      {isDone && job?.googleAlbumUrl && (
        <a
          href={job.googleAlbumUrl}
          target="_blank"
          rel="noopener noreferrer"
          className={styles['viewGpBtn']}
        >
          <ExternalLink size={14} />
          View in Google Photos
        </a>
      )}

      {/* Actions */}
      <div className={styles['actionRow']}>
        {isActive && (
          <motion.button
            type="button"
            onClick={onCancel}
            disabled={cancelling}
            className={styles['stopBtn']}
            whileTap={{ scale: 0.97 }}
          >
            <StopCircle size={13} />
            {cancelling ? 'Stopping…' : 'Stop sync'}
          </motion.button>
        )}
        {isDone && (
          <motion.button
            type="button"
            onClick={onSyncMore}
            className={styles['syncMoreBtn']}
            whileTap={{ scale: 0.97 }}
          >
            <GooglePhotosLogo size={13} />
            Sync more photos
          </motion.button>
        )}
        {(isFailed || isCancelled) && (
          <motion.button
            type="button"
            onClick={onRetry}
            className={styles['retryBtn']}
            whileTap={{ scale: 0.97 }}
          >
            <RefreshCw size={13} />
            Try again
          </motion.button>
        )}
        <motion.button
          type="button"
          onClick={onClose}
          className={styles['doneBtn']}
          whileTap={{ scale: 0.97 }}
        >
          {isDone ? 'Done' : 'Close'}
        </motion.button>
      </div>
    </div>
  );
}
